/**
 * Utilities for merging extracted data into output data.
 *
 * These functions handle the merge logic when AI extraction completes
 * and the user wants to accept the extracted values.
 */

/**
 * Check if both values are non-array objects suitable for recursive merge
 */
function shouldRecursiveMerge(
  currentValue: unknown,
  extractedValue: unknown,
): currentValue is Record<string, unknown> {
  return (
    typeof extractedValue === 'object' &&
    extractedValue !== null &&
    !Array.isArray(extractedValue) &&
    typeof currentValue === 'object' &&
    currentValue !== null &&
    !Array.isArray(currentValue)
  );
}

/**
 * Merges extracted data into output data.
 *
 * IMPORTANT: Iterates over outputData keys (not extractedData) to preserve schema.
 *
 * Rules per field:
 * 1. If value exists in only outputData → keep outputData value
 * 2. If value exists in only extractedData → use extractedData value
 * 3. If value exists in both → extractedData wins
 * 4. If both null/undefined → keep null/undefined
 *
 * @param outputData - The current output data (defines the schema)
 * @param extractedData - The AI-extracted data to merge in
 * @returns Merged data object with extractedData values taking precedence
 */
export function mergeExtractedData(
  outputData: Record<string, unknown> | null | undefined,
  extractedData: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  // If no output data schema, nothing to merge into
  if (!outputData) return {};
  // If no extracted data, return output as-is
  if (!extractedData) return { ...outputData };

  const result = { ...outputData };

  // Iterate over OUTPUT DATA keys (preserves schema)
  for (const key of Object.keys(outputData)) {
    const currentValue = outputData[key];
    const extractedValue = extractedData[key];

    // Skip if extracted value is null/undefined - keep current value
    if (extractedValue === null || extractedValue === undefined) {
      continue;
    }

    // Recursive merge for nested objects (not arrays)
    if (shouldRecursiveMerge(currentValue, extractedValue)) {
      result[key] = mergeExtractedData(currentValue, extractedValue as Record<string, unknown>);
    } else {
      result[key] = extractedValue; // Extracted wins
    }
  }

  return result;
}

/**
 * Checks if there are any differences between the current output data
 * and what would result from merging the extracted data.
 *
 * This is used to determine whether to show the "Accept Extracted Data" button.
 *
 * @param outputData - The current output data
 * @param extractedData - The AI-extracted data
 * @returns true if merging would change the output data
 */
export function hasExtractedDataChanges(
  outputData: Record<string, unknown> | null | undefined,
  extractedData: Record<string, unknown> | null | undefined,
): boolean {
  if (!extractedData || !outputData) return false;
  return (
    JSON.stringify(mergeExtractedData(outputData, extractedData)) !== JSON.stringify(outputData)
  );
}
